import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Autocomplete, Box, FilterOptionsState } from '@mui/material';
import { ReactComponent as UndoIcon } from '../../assets/16x16/undo.svg';
import { ReactComponent as CloseIcon } from '../../assets/close-2.svg';
import { ReactComponent as PlusIcon } from '../../assets/plus-2.svg';
import { distinctArray } from '../../utils/common';
import Button from '../Button';
import Chip from '../Chip';
import { Flex } from '../Flex';
import TextField from '../TextField';

const Wrapper = styled(Flex)`
  padding: 8px 0;
  gap: 8px 12px;
  align-items: center;
  min-height: 40px;
  flex-wrap: wrap;
`;

const CloseIconStyled = styled(CloseIcon)`
  width: 10px;
`;

const UndoIconStyled = styled(UndoIcon)`
  path {
    fill: ${({ theme }) => theme.palette.common.slate};
  }
`;

export type Props = {
  value?: string[];
  onChange?: (val: string[]) => void;
  renderer?: (val: string) => React.ReactNode;
  optionRenderer?: (val: string) => React.ReactNode;
  buttonText: string;
  placeholder?: string;
  autoCompleteOptions?: string[];
  readOnly?: boolean;
  newValues?: string[];
  deletedValues?: string[];
  className?: string;
};

const ChipList = React.forwardRef(
  (
    {
      value,
      onChange,
      renderer,
      optionRenderer,
      buttonText,
      placeholder,
      autoCompleteOptions = [],
      readOnly,
      newValues,
      deletedValues,
      className,
    }: Props,
    ref: any,
  ) => {
    const [editMode, setEditMode] = useState<boolean>(false);
    const [typedValue, setTypedValue] = useState<string>('');

    const inputRef = useRef<any>();

    const distinctOptions = useMemo(
      () => distinctArray(autoCompleteOptions).sort(),
      [autoCompleteOptions],
    );

    const nonDeletedValues =
      value?.filter((item) => !deletedValues?.includes(item)) || [];

    const handleDelete = (index: number) => {
      if (value) {
        onChange?.(
          (value || []).filter(
            (val, i) => index !== i && !deletedValues?.includes(val),
          ),
        );
      }
    };

    const handleUndo = (index: number) => {
      if (value) {
        onChange?.(
          (value || []).filter(
            (val, i) =>
              index === i || (!deletedValues?.includes(val) && index !== i),
          ),
        );
      }
    };

    const handleKeyDown = (e: any) => {
      if (e.key === 'Escape') {
        setEditMode(false);
        setTypedValue('');
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        setEditMode(false);
      }
    };

    const handleChange = (_: any, newValue: string | null) => {
      const val = newValue?.trim();

      if (val && !value?.includes(val)) {
        onChange?.([...nonDeletedValues, val]);
        setEditMode(false);
        setTypedValue('');
      }
    };

    const handleAddClick = () => {
      setEditMode(true);
      inputRef.current?.focus();
    };

    const handleBlur = () => {
      const trimmedVal = typedValue.trim();
      if (trimmedVal.length > 0 && !value?.includes(trimmedVal)) {
        onChange?.([...nonDeletedValues, trimmedVal]);
      }
      setEditMode(false);
      setTypedValue('');
    };

    const filterOptions = (
      options: string[],
      state: FilterOptionsState<string>,
    ) => {
      const val = state.inputValue.toLowerCase();
      const nonSelectedOptions = options.filter((o) => !value?.includes(o));

      const startsWith = nonSelectedOptions.filter((o) =>
        o.toLowerCase().startsWith(val),
      );

      const includes = nonSelectedOptions.filter(
        (o) =>
          o.toLowerCase().includes(val) &&
          !startsWith.find((s) => s.toLowerCase() === o.toLowerCase()),
      );

      return [...startsWith, ...includes];
    };

    const getDeleteIcon = (item: string) =>
      deletedValues?.includes(item) ? (
        <UndoIconStyled width={14} />
      ) : (
        <CloseIconStyled />
      );

    const handleDeleteClicked = (item: string, index: number) =>
      readOnly
        ? undefined
        : deletedValues?.includes(item)
        ? handleUndo(index)
        : handleDelete(index);

    const getChipVariant = (item: string) =>
      newValues?.includes(item)
        ? 'dashed'
        : deletedValues?.includes(item)
        ? 'crossed'
        : undefined;

    return (
      <Wrapper aria-label="chiplist" ref={ref} className={className}>
        {value?.map((item, i) => (
          <Chip
            key={`${item}${i}`}
            label={renderer ? renderer(item) : item}
            deleteIcon={getDeleteIcon(item)}
            onDelete={readOnly ? undefined : () => handleDeleteClicked(item, i)}
            customVariant={getChipVariant(item)}
          />
        ))}
        {!editMode && !readOnly && (
          <Button
            color="secondary"
            startIcon={<PlusIcon />}
            onClick={handleAddClick}
            size="extraSmall"
          >
            {buttonText}
          </Button>
        )}

        {!readOnly && (
          <Autocomplete
            sx={{
              width: editMode ? '152px' : 0,
              minWidth: 0,
              overflow: 'hidden',
              opacity: editMode ? 1 : 0,
              pointerEvents: editMode ? undefined : 'none',
            }}
            freeSolo
            options={distinctOptions}
            onChange={handleChange}
            openOnFocus
            onInputChange={(e: any) => setTypedValue(e?.target.value || '')}
            inputValue={typedValue}
            placeholder={placeholder}
            onKeyDown={handleKeyDown}
            componentsProps={{ popper: { sx: { width: 'auto !important' } } }}
            onBlur={handleBlur}
            renderOption={
              optionRenderer
                ? (props, option) => (
                    <Box component="li" {...props}>
                      {optionRenderer(option)}
                    </Box>
                  )
                : undefined
            }
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            renderInput={({ size: _muiSize, ...params }) => (
              <TextField {...params} inputRef={inputRef} />
            )}
            filterOptions={filterOptions}
          />
        )}
      </Wrapper>
    );
  },
);

export default ChipList;
